import { daysDifference } from '@/_utils/days-difference';
import { DataBagService } from '@/common/data-bag.service';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { Result } from '@/reservation/reservation.model';
import { DatePipe } from '@angular/common';
import { Component, DestroyRef, effect, OnInit, signal, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs';
import { BagTransportFiltersComponent } from './bag-transport-filters/bag-transport-filters.component';
import { BagTransportTermApiService } from './bag-transport-term-api.service';
import { BagTransportTermModalTransportComponent } from './bag-transport-term-modal-transport/bag-transport-term-modal-transport.component';
import { TourismEvents, TransportTermList } from './bag-transport-term.model';
import { TransportSide, TransportTerm } from '@/new-transport/_models/transport.model';

import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { ProcessBagTransportTermData } from '@/bag/bag-transport-term/process-bag-transport-term-data';
import { encodeURIParams } from '@/_utils/encode-uri-params';
import { filtersToString } from '@/_utils/filters-to-string';
import { PageEvent } from '@angular/material/paginator';
import { TransportTermMatDataService } from './transport-term-mat-data.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TransportPlaneImportDialogComponent } from './transport-plane-import-dialog/transport-plane-import-dialog.component';

const DEFAULT_ORDERING = 'accommodation__startswith';

@Component({
	selector: 'app-bag-transport-term',
	templateUrl: './bag-transport-term.component.html',
	styleUrls: ['./bag-transport-term.component.scss'],
})
export class BagTransportTermComponent implements OnInit {
	readonly TransportSide = TransportSide;
	readonly bagTransportFilters = viewChild(BagTransportFiltersComponent);

	filters: Params = {
		departure_time_after: null,
		departure_time_before: null,
		name: null,
		return_time_after: null,
		return_time_before: null,
		accommodation_after: null,
		accommodation_before: null,
		status: null,
		swinging: null,
		transport_departure_exists: null,
		transport_return_exists: null,
		kind: [],
		responsible_user: [],
		ordering: DEFAULT_ORDERING,
	};
	action_term = true;
	transportExist = false;
	sameTransport = false;
	transport = null;
	stop_sale = false;

	readonly search = signal('');

	readonly search$ = toObservable(this.search);

	readonly parameters = {
		yesno: [
			{ name: 'Tak', value: 'true' },
			{ name: 'Nie', value: 'false' },
		],
		names: [],
		statuses: [
			{ value: 100, viewValue: '100%' },
			{ value: 75, viewValue: '75%' },
			{ value: 50, viewValue: '50%' },
			{ value: 25, viewValue: '25%' },
			{ value: 0, viewValue: '0%' },
		],
		organizationStatuses: [],
		allUsers: [],
	};

	readonly responsibleUserHasChanged = signal(false);

	page = signal(0);
	pageSize = signal(50);
	allItemsSize = signal(0);
	transportTermCache = signal<{ [key: number]: TransportTermList[] }>({});
	isLoading = signal(false);

	constructor(
		protected dialog: MatDialog,
		protected api: Data2Service,
		protected bag: DataBagService,
		protected date: DateService,
		protected infobox: InfoboxService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected question: QuestionService,
		protected loader: LoaderService,
		private readonly datePipe: DatePipe,
		private readonly bagTransportTermApiService: BagTransportTermApiService,
		private readonly matDataService: TransportTermMatDataService,
		private readonly destroyRef: DestroyRef,
	) {
		effect(() => {
			this.search$
				.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.searchChangeFilterSet());
		});
	}

	ngOnInit(): void {
		this.bag.get(
			'/transport/transport_term_name/?limit=999',
			(result: Result<{ name: string; productId: number }>, _) => {
				this.parameters.names = result.results;
			},
		);
		this.bag.get('/profile/all_users/?limit=999', (result, error) => {
			this.parameters.allUsers = result.results.filter((user) => !!user.firstName && !!user.lastName);
		});
		this.bag.get('/transport/transport_term_organization_status/?limit=999', (result, error) => {
			this.parameters.organizationStatuses = result.results;
		});
	}

	goToTransport(side: TransportSide): void {
		const checkedElements = this.matDataService.matData.data.filter((element) => element.checked);

		const isPlane = checkedElements.some((v) => v.kind === OfferTransportKind.PLANE);

		if (isPlane && checkedElements.length >= 2) {
			this.infobox.error('Możesz wybrać tylko jeden termin samolotowy na raz!');
			return;
		}

		if (!checkedElements.length) {
			this.infobox.error('Nie zaznaczono niczego');
			return;
		}

		const propertyName = side === TransportSide.DEPARTURE ? 'transportDeparture' : 'transportReturn';
		if (checkedElements.length > 1 && checkedElements.some((v) => !v[propertyName])) {
			this.infobox.error(
				'Nie można przejść do łączenia transportów. Przynajmniej jedno wydarzenie nie ma przypisanego transportu',
			);
			return;
		}

		const isDifferenceValid: boolean = this.isDaysDifferenceValid(checkedElements, side);

		if (!isDifferenceValid) {
			this.infobox.error(
				'Nie można łączyć transportów jeśli rożnica dni między wydarzeniami jest większa lub równa 2',
			);
			return;
		}

		this.transportExist = false;

		this.transport = null;
		this.sameTransport = false;

		this.transportExist = this.checkTransportExits(checkedElements, side);
		const terms = this.getTransportTerms(checkedElements);

		if (this.transportExist == true) {
			this.checkIfSelectedSameTransports(checkedElements, side);
		}

		const linkedTransportIds = checkedElements.map((v) => v[propertyName]?.linkedTransport?.id).filter((v) => !!v);
		if (checkedElements.length > 1 && linkedTransportIds.length > 0) {
			this.question
				.ask('Co najmniej jedno z tych wydarzeń jest już połączone, czy chcesz kontynuować?')
				.pipe(take(1))
				.subscribe(({ result }) => {
					if (!result) {
						return;
					}

					this.openTransportTermModalTransport(
						checkedElements,
						side,
						terms,
						linkedTransportIds,
						isPlane ? OfferTransportKind.PLANE : OfferTransportKind.COACH,
					);
				});
		} else {
			this.openTransportTermModalTransport(
				checkedElements,
				side,
				terms,
				linkedTransportIds,
				isPlane ? OfferTransportKind.PLANE : OfferTransportKind.COACH,
			);
		}
	}

	checkTransportExits(checkedElements: TransportTermList[], side: TransportSide): boolean {
		return checkedElements.some((v) => {
			if (side === TransportSide.DEPARTURE) return !!v.transportDeparture;
			else return !!v.transportReturn;
		});
	}

	getTransportTerms(checkedElements: TransportTermList[]): TransportTerm[] {
		const terms = [];
		for (let i = 0; i < checkedElements.length; i++) {
			terms.push(checkedElements[i].id);
		}
		return terms;
	}

	checkIfSelectedSameTransports(checkedElements: TransportTermList[], side: TransportSide): void {
		const property = side === TransportSide.DEPARTURE ? 'transportDeparture' : 'transportReturn';

		if (checkedElements.length === 1) {
			this.transport = checkedElements[0][property].id;
			this.sameTransport = true;

			return;
		}

		if (new Set(checkedElements.map((v) => v[property].id))) {
			this.transport = null;
			this.sameTransport = false;
		} else {
			this.transport = checkedElements[0][property].id;
			this.sameTransport = true;
		}
	}

	openTransportTermModalTransport(
		elements: TransportTermList[],
		side: TransportSide,
		terms: TransportTerm[],
		linkedTransportIds: number[] = [],
		kind: OfferTransportKind,
	): void {
		this.dialog
			.open(BagTransportTermModalTransportComponent, {
				autoFocus: false,
				minWidth: '45%',
				data: {
					transport: this.transport,
					exist: this.transportExist,
					same: this.sameTransport,
					elements,
					side,
					terms,
					linkedTransports: linkedTransportIds,
					transportKind: kind,
				},
			})
			.afterClosed()
			.pipe(filter((v) => !!v))
			.subscribe(() => {
				this.loadData();
			});
	}

	filtersSelected(data: Params): void {
		this.filters = { ...data, search: this.search() };

		this.reloadData();
	}

	filtersOpen(): void {
		this.route.queryParams.pipe(take(1)).subscribe((params) => {
			if (!params || Object.keys(params).length === 0) {
				return;
			}
			const filters: Params = {};
			for (const key in params) {
				filters[key] = this.bagTransportFilters().convertParm(key, params[key]);
			}
			this.filters = filters;
			this.bagTransportFilters().filtersOpen();
		});
	}

	pageChanged(page: PageEvent): void {
		this.page.set(page.pageIndex);
		this.pageSize.set(page.pageSize);
		window.scrollTo({ top: 0, behavior: 'smooth' });
		this.loadData();
	}

	deadlineChanged(): void {
		this.transportTermCache.update((current) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { [this.page()]: _, ...rest } = current;
			return rest;
		});

		this.loadData();
	}

	setSorting(ordering: string): void {
		this.filters.ordering = ordering;
		this.reloadData();
	}

	searchChangeFilterSet(): void {
		const searchTerm = this.search();

		if (searchTerm.length < 3) {
			if (this.filters['search']) {
				this.filters['search'] = null;
				this.reloadData();
			}
			return;
		}

		if (this.filters['search'] !== searchTerm) {
			this.filters['search'] = searchTerm;
			this.reloadData();
		}
	}

	openPlaneImportDialog(): void {
		this.dialog
			.open(TransportPlaneImportDialogComponent, {
				autoFocus: false,
				minWidth: '45%',
				height: '700px',
			})
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => this.reloadData());
	}

	private reloadData(): void {
		this.transportTermCache.set({});
		this.page.set(0);
		this.loadData();
	}

	/**
	 * Transports can only be linked if the difference in days between selected events is less than 2 days.
	 * A warning is displayed if the difference is exactly one day.
	 */
	private isDaysDifferenceValid(checkedElements: TransportTermList[], side: TransportSide): boolean {
		const dates = checkedElements
			.map((v: TransportTermList) => (side === TransportSide.DEPARTURE ? v.departureTime : v.returnTime))
			.sort((a, b) => a.localeCompare(b));

		if (dates.length > 1) {
			const firstDate = new Date(dates[0]);
			const lastDate = new Date(dates[dates.length - 1]);

			const difference = daysDifference(firstDate, lastDate);

			if (difference === 1) {
				this.infobox.warning(
					'Różnica między tymi wydarzeniami wynosi jedeń dzień, czy na pewno chcesz kontynuować?',
				);
			}
			return difference <= 1;
		}
		return true;
	}

	private getFiltersParams(filters = this.filters): string {
		return encodeURIParams(filters);
	}

	private loadData(): void {
		this.isLoading.set(true);
		this.matDataService.matData.data = [];
		this.filters.limit = this.pageSize();
		this.filters.offset = this.page() * this.pageSize();
		const params = this.getFiltersParams();
		if (this.transportTermCache()[this.page()]) {
			this.matDataService.matData.data = this.transportTermCache()[this.page()];
			this.isLoading.set(false);
			return;
		}
		this.bagTransportTermApiService.getTransportTerm(params).subscribe((terms) => {
			this.convertData(terms);
		});
	}

	private convertData(data: Result<TransportTermList>): void {
		const payload = {
			kind: 'PROGRAM',
			tourismEvents: this.groupTourismEventId(data),
		};
		this.api.command('/Bag/BagTourismEventsGuides/', payload, (result, _) => {
			if (data) {
				this.allItemsSize.set(data.count);
				this.matDataService.matData.data = new ProcessBagTransportTermData(this.datePipe).afterConvertData(
					data,
					result.data,
				);
				this.transportTermCache.set({
					...this.transportTermCache(),
					[this.page()]: this.matDataService.matData.data,
				});
				this.isLoading.set(false);
			}
		});
	}

	private groupTourismEventId(data: Result<TransportTermList>): TourismEvents[] {
		const flatMap = data.results.flatMap((obj) => obj.termtourismeventSet);
		return [...new Map(flatMap.map((obj) => [obj.eventId, obj])).values()];
	}
}
